"use client";

import React, { useState, useMemo, useRef, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

interface PrestataireSerialized {
  id: string;
  slug: string;
  nom: string;
  titre: string;
  specialite: string;
  specialites: string[];
  photoUrl: string;
  photoAlt: string;
  centreNom: string;
  centreCommune: string;
  centreSlug: string;
  langues: string[];
  centres: { nom: string; slug: string; commune: string }[];
  specialiteBadge: { bg: string; text: string; border: string };
}

interface Props {
  prestataires: PrestataireSerialized[];
  allSpecialites: string[];
  allCentres: string[];
  allLangues: string[];
}

/* ── Multi-select pill dropdown (desktop) ────────────────── */
function PillGroup({
  label,
  icon,
  options,
  selected,
  onToggle,
  onClear,
}: {
  label: string;
  icon: React.ReactNode;
  options: string[];
  selected: string[];
  onToggle: (v: string) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const count = selected.length;
  const isActive = count > 0;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer border ${
          isActive
            ? 'bg-primary text-white border-primary shadow-sm'
            : 'bg-white text-text-main border-gray-200 hover:border-primary/40 hover:shadow-sm'
        }`}
        style={{ fontFamily: 'Poppins, sans-serif' }}
      >
        {icon}
        <span>{label}</span>
        {isActive && (
          <span className="flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-white text-primary text-[11px] font-bold">
            {count}
          </span>
        )}
        <svg
          width="12" height="12" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          className={`transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {/* Options are always mounted (visibility-toggled) so the translation layer picks them up. */}
      <div
        className={`absolute top-full left-0 mt-2 bg-white rounded-xl border border-gray-100 shadow-xl p-2 z-40 min-w-[240px] max-h-[300px] overflow-y-auto transition-opacity ${
          open ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
        }`}
        aria-hidden={!open}
      >
        {isActive && (
          <button
            onClick={onClear}
            className="w-full text-left px-3 py-2 rounded-lg text-sm font-medium text-text-muted hover:bg-gray-50 transition-colors cursor-pointer mb-1"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Réinitialiser
          </button>
        )}
        {options.map((opt) => {
          const checked = selected.includes(opt);
          return (
            <label
              key={opt}
              className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors ${
                checked ? 'bg-primary/10 text-primary' : 'text-text-main hover:bg-gray-50'
              }`}
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              <span
                className={`flex items-center justify-center w-4 h-4 rounded border transition-colors flex-shrink-0 ${
                  checked ? 'bg-primary border-primary' : 'bg-white border-gray-300'
                }`}
              >
                {checked && (
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                )}
              </span>
              <input type="checkbox" className="sr-only" checked={checked} onChange={() => onToggle(opt)} />
              {opt}
            </label>
          );
        })}
      </div>
    </div>
  );
}

/* ── Main component ──────────────────────────────────────── */
export default function PrestatairesFilter({ prestataires, allSpecialites, allCentres, allLangues }: Props) {
  const [specFilter, setSpecFilter] = useState<string[]>([]);
  const [centreFilter, setCentreFilter] = useState<string[]>([]);
  const [langueFilter, setLangueFilter] = useState<string[]>([]);
  const [mobileOpen, setMobileOpen] = useState(false);

  const toggle = (setter: React.Dispatch<React.SetStateAction<string[]>>) => (v: string) =>
    setter((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));

  const filtered = useMemo(() => {
    return prestataires.filter((p) => {
      const matchSpec = specFilter.length === 0 || p.specialites.some((s) => specFilter.includes(s));
      const matchCentre = centreFilter.length === 0 || p.centres.some((c) => centreFilter.includes(c.nom));
      const matchLangue = langueFilter.length === 0 || p.langues.some((l) => langueFilter.includes(l));
      return matchSpec && matchCentre && matchLangue;
    });
  }, [prestataires, specFilter, centreFilter, langueFilter]);

  const activeFiltersCount = specFilter.length + centreFilter.length + langueFilter.length;

  const clearAll = () => {
    setSpecFilter([]);
    setCentreFilter([]);
    setLangueFilter([]);
  };

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const specIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </svg>
  );
  const centreIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
    </svg>
  );
  const langueIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );

  /* ── Mobile multi-select group ── */
  const MobileGroup = ({
    label,
    icon,
    options,
    selected,
    onToggle,
  }: {
    label: string;
    icon: React.ReactNode;
    options: string[];
    selected: string[];
    onToggle: (v: string) => void;
  }) => (
    <div className="mb-5">
      <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2.5 flex items-center gap-1.5" style={{ fontFamily: 'Poppins, sans-serif' }}>
        {icon} {label}
      </p>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const checked = selected.includes(opt);
          return (
            <button
              key={opt}
              onClick={() => onToggle(opt)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer border ${
                checked
                  ? 'bg-primary text-white border-primary'
                  : 'bg-white text-text-main border-gray-200 hover:border-primary/40'
              }`}
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );

  const chips: { value: string; onRemove: () => void }[] = [
    ...specFilter.map((v) => ({ value: v, onRemove: () => setSpecFilter((p) => p.filter((x) => x !== v)) })),
    ...centreFilter.map((v) => ({ value: v, onRemove: () => setCentreFilter((p) => p.filter((x) => x !== v)) })),
    ...langueFilter.map((v) => ({ value: v, onRemove: () => setLangueFilter((p) => p.filter((x) => x !== v)) })),
  ];

  return (
    <>
      {/* ═══ FILTER BAR ═══ */}
      <section className="bg-white border-b border-gray-100 sticky top-16 md:top-20 z-30">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-3 md:py-4">

          {/* ── Desktop: pill triggers ── */}
          <div className="hidden md:flex items-center gap-2.5 flex-wrap">
            <PillGroup label="Spécialisation" icon={specIcon} options={allSpecialites} selected={specFilter} onToggle={toggle(setSpecFilter)} onClear={() => setSpecFilter([])} />
            <PillGroup label="Centre" icon={centreIcon} options={allCentres} selected={centreFilter} onToggle={toggle(setCentreFilter)} onClear={() => setCentreFilter([])} />
            <PillGroup label="Langue" icon={langueIcon} options={allLangues} selected={langueFilter} onToggle={toggle(setLangueFilter)} onClear={() => setLangueFilter([])} />

            {activeFiltersCount > 0 && (
              <button
                onClick={clearAll}
                className="flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium text-primary hover:bg-primary/10 transition-all cursor-pointer"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
                Effacer tout
              </button>
            )}

            <span className="ml-auto text-xs text-text-muted" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {filtered.length} résultat{filtered.length > 1 ? 's' : ''}
            </span>
          </div>

          {/* ── Mobile: compact trigger ── */}
          <div className="flex md:hidden items-center justify-between">
            <button
              onClick={() => setMobileOpen(true)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer border ${
                activeFiltersCount > 0
                  ? 'bg-primary text-white border-primary'
                  : 'bg-white text-text-main border-gray-200'
              }`}
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="4" y1="6" x2="20" y2="6" /><line x1="8" y1="12" x2="20" y2="12" /><line x1="12" y1="18" x2="20" y2="18" />
              </svg>
              Filtres
              {activeFiltersCount > 0 && (
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-white text-primary text-xs font-bold">
                  {activeFiltersCount}
                </span>
              )}
            </button>
            <span className="text-xs text-text-muted" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {filtered.length} résultat{filtered.length > 1 ? 's' : ''}
            </span>
          </div>

          {/* ── Active filter chips (desktop + mobile) ── */}
          {chips.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2.5">
              {chips.map((chip) => (
                <span key={chip.value} className="inline-flex items-center gap-1 bg-primary/10 text-primary rounded-full px-2.5 py-1 text-xs font-medium" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {chip.value}
                  <button onClick={chip.onRemove} className="hover:bg-primary/20 rounded-full p-0.5 transition-colors cursor-pointer" aria-label={`Retirer le filtre ${chip.value}`}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ═══ MOBILE DRAWER ═══ */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />

          {/* Bottom sheet */}
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl max-h-[80vh] flex flex-col animate-slide-up">
            {/* Handle + header */}
            <div className="flex-shrink-0 pt-3 pb-4 px-5 border-b border-gray-100">
              <div className="w-10 h-1 rounded-full bg-gray-300 mx-auto mb-4" />
              <div className="flex items-center justify-between">
                <h3 className="text-base font-bold text-text-main" style={{ fontFamily: 'Poppins, sans-serif' }}>Filtrer les prestataires</h3>
                <button
                  onClick={() => setMobileOpen(false)}
                  className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-text-muted hover:text-text-main transition-colors cursor-pointer"
                  aria-label="Fermer les filtres"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
                </button>
              </div>
            </div>

            {/* Scroll area */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <MobileGroup label="Spécialisation" icon={specIcon} options={allSpecialites} selected={specFilter} onToggle={toggle(setSpecFilter)} />
              <MobileGroup label="Centre" icon={centreIcon} options={allCentres} selected={centreFilter} onToggle={toggle(setCentreFilter)} />
              <MobileGroup label="Langue" icon={langueIcon} options={allLangues} selected={langueFilter} onToggle={toggle(setLangueFilter)} />
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 px-5 py-4 border-t border-gray-100 flex items-center gap-3">
              {activeFiltersCount > 0 && (
                <button
                  onClick={clearAll}
                  className="flex-1 py-2.5 rounded-xl text-sm font-medium text-primary border border-primary/30 hover:bg-primary/5 transition-all cursor-pointer"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  Tout effacer
                </button>
              )}
              <button
                onClick={() => setMobileOpen(false)}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-primary text-white hover:bg-primary/90 transition-all cursor-pointer"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                Voir {filtered.length} résultat{filtered.length > 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ GRID ═══ */}
      <section className="py-12 md:py-20 bg-gray-50/50">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          {filtered.length === 0 ? (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
                </svg>
              </div>
              <p className="text-text-muted font-medium mb-1" style={{ fontFamily: 'Poppins, sans-serif' }}>Aucun prestataire trouvé</p>
              <p className="text-text-muted text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>Essayez de modifier vos filtres pour élargir la recherche.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
              {filtered.map((p) => (
                <Link
                  key={p.slug}
                  href={`/prestataires/${p.slug}`}
                  className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg hover:border-primary/20 transition-all duration-300 overflow-hidden flex flex-row"
                  aria-label={`Voir le profil de ${p.titre} ${p.nom}`}
                >
                  {/* Square photo */}
                  <div className="relative w-28 h-28 sm:w-36 sm:h-36 md:w-40 md:h-40 flex-shrink-0 bg-gray-100 overflow-hidden">
                    <AppImage
                      src={p.photoUrl}
                      alt={p.photoAlt}
                      fill
                      className="object-cover object-[center_20%] group-hover:scale-105 transition-transform duration-500"
                      sizes="160px"
                    />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0 p-4 sm:p-5 flex flex-col justify-center">
                    <span className={`inline-flex self-start items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-semibold ${p.specialiteBadge.bg} ${p.specialiteBadge.text} border ${p.specialiteBadge.border} mb-2`} style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {p.specialite}
                    </span>

                    <h2 className="font-bold text-text-main text-base sm:text-lg leading-tight mb-1 group-hover:text-primary transition-colors truncate" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {p.titre} {p.nom}
                    </h2>

                    <p className="text-text-muted text-xs sm:text-sm mb-2.5 flex items-start gap-1.5" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#DB052C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5" aria-hidden="true">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                      </svg>
                      <span>{p.centreNom}{p.centreCommune ? ` · ${p.centreCommune}` : ''}</span>
                    </p>

                    {/* Langues */}
                    <div className="flex flex-wrap gap-1 sm:gap-1.5">
                      {p.langues.map((lang) => (
                        <span key={lang} className="text-[10px] sm:text-xs bg-gray-100 text-text-muted rounded-full px-2 py-0.5" style={{ fontFamily: 'Poppins, sans-serif' }}>
                          {lang}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="hidden sm:flex items-center pr-4 flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-gray-50 group-hover:bg-primary/10 flex items-center justify-center transition-colors">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-text-muted group-hover:text-primary transition-colors" aria-hidden="true">
                        <path d="M9 18l6-6-6-6" />
                      </svg>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
